import Box from '@mui/material/Box'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams, useSearchParams } from 'react-router-dom-v5-compat'

import { useLazyGetAccountMetaQuery } from 'api/meta/getAccountMeta'
import NavbarWrapper from 'components/common/NavbarWrapper'
import TabPanel from 'components/common/TabPanel'
import AccountTab from 'components/customer-details/account'
import ActivityTab from 'components/customer-details/activity/ActivityTab'
import BillingTab from 'components/customer-details/billing'
import FeesTab from 'components/customer-details/fees'
import Header from 'components/customer-details/layout/Header'
import ServicesTab from 'components/customer-details/services'
import { CUSTOMER_DETAILS_TABS_ID, CUSTOMER_DETAILS_TABS_ID_LIST } from 'containers/customer-details/settings'
import { getCustomerDetails } from 'middleware/actions/customers'
import RoutingActionsProvider from 'providers/route_manager/RoutingActionsProvider'
import { onChangeLocationId } from 'slices/customer/customerDetailsSlice'
import { get } from 'utils/lodash'

import './style.scss'

const { ACCOUNT_TAB_ID, SERVICES_TAB_ID, BILLING_TAB_ID, FEES_TAB_ID, ACTIVITY_TAB_ID } = CUSTOMER_DETAILS_TABS_ID

const TAB_PANEL_SX = { p: 0, '&:not([hidden])': { flex: 1, minHeight: 0, overflow: 'hidden' } }

type CustomerLocation = { id: string; deactivated?: boolean }

type CustomerAccount = { id: string; locations?: CustomerLocation[] }

const CustomerDetails = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [getAccountMeta] = useLazyGetAccountMetaQuery()

  // Cast, not a union: it is null until the account lands, and narrowing would let the tabs swap that null for their {} default.
  const customerDetails = useSelector(
    (state: Record<string, unknown>) => get(state, 'CustomersReducer.customerDetails', null) as CustomerAccount
  )
  const currentTab = useSelector((state: Record<string, unknown>) => get(state, 'CustomerDetails.currentTab', ACCOUNT_TAB_ID) as string)
  // This param is important to avoid duplicate API calls
  const [isPageReady, setIsPageReady] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const { accountId } = useParams()
  const urlTabId = (searchParams.get('tab') || '').toLowerCase()
  const urlLocationId = searchParams.get('location') || ''

  const locations = get(customerDetails, 'locations', []) as CustomerLocation[]

  // `locations` has to stay in the deps: a stale copy fails to match a location added this session and falls back to All.
  const handleLocationChange = useCallback(
    (id = '', isInitialLoad = false, allLocations: CustomerLocation[] = locations) => {
      const isAccountType = !id || id === accountId || !allLocations.find(loc => loc.id === id)

      // Reset location dropdown
      dispatch(onChangeLocationId(isAccountType ? '' : id))
      setIsPageReady(true)

      if (!CUSTOMER_DETAILS_TABS_ID_LIST.includes(urlTabId) && isInitialLoad) {
        searchParams.set('tab', currentTab)
        setSearchParams(searchParams, { replace: true, state: get(location, 'state') })
      }
      if (isAccountType) {
        searchParams.delete('location')
      } else {
        searchParams.set('location', id)
      }
      setSearchParams(searchParams, { replace: true, state: get(location, 'state') })
    },
    [accountId, currentTab, dispatch, location, locations, searchParams, setSearchParams, urlTabId]
  )

  const handleCustomerDetailsSuccess = useCallback(
    (account: CustomerAccount) => {
      let locationId = urlLocationId
      const allLocations = get(account, 'locations', []) as CustomerLocation[]
      const activeLocations = allLocations.filter(loc => !loc?.deactivated)

      // If location id is not present in the url and account has single location then pre select
      if (!locationId && activeLocations.length === 1) {
        locationId = get(activeLocations, '[0].id', '') as string
        if (locationId) {
          searchParams.set('location', locationId)
        }
        setSearchParams(searchParams, { replace: true, state: get(location, 'state') })
      }

      handleLocationChange(locationId || accountId, true, allLocations)
    },
    [accountId, handleLocationChange, location, searchParams, setSearchParams, urlLocationId]
  )

  useEffect(() => {
    setIsPageReady(false)
    // Call customer details API
    dispatch(getCustomerDetails({ accountId, isInitialCall: true, addRecent: true }, handleCustomerDetailsSuccess))
    // Call customer meta, used by inner components
    getAccountMeta(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the account is refetched per route, not per callback identity
  }, [accountId])

  useEffect(() => {
    if (urlTabId && isPageReady) {
      handleLocationChange(urlLocationId || accountId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleLocationChange writes the search params it depends on, so it cannot gate its own effect
  }, [urlTabId, isPageReady])

  return (
    <NavbarWrapper innerSx={{ overflow: { lg: 'hidden' } }}>
      <Box className="customer-details-container" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header accountId={accountId} isPageReady={isPageReady} />
        {isPageReady && (
          <RoutingActionsProvider>
            <TabPanel index={ACCOUNT_TAB_ID.toLowerCase()} sx={TAB_PANEL_SX} value={urlTabId}>
              <AccountTab customerDetails={customerDetails} selectedTab={urlTabId} />
            </TabPanel>
            <TabPanel index={SERVICES_TAB_ID.toLowerCase()} sx={TAB_PANEL_SX} value={urlTabId}>
              <ServicesTab customerDetails={customerDetails} selectedTab={urlTabId} onLocationChange={handleLocationChange} />
            </TabPanel>
            <TabPanel index={BILLING_TAB_ID.toLowerCase()} sx={TAB_PANEL_SX} value={urlTabId}>
              <BillingTab customerDetails={customerDetails} selectedTab={urlTabId} />
            </TabPanel>
            <TabPanel index={FEES_TAB_ID.toLowerCase()} sx={TAB_PANEL_SX} value={urlTabId}>
              <FeesTab customerDetails={customerDetails} selectedTab={urlTabId} />
            </TabPanel>
            <TabPanel index={ACTIVITY_TAB_ID.toLowerCase()} sx={TAB_PANEL_SX} value={urlTabId}>
              <ActivityTab customerDetails={customerDetails} />
            </TabPanel>
          </RoutingActionsProvider>
        )}
      </Box>
    </NavbarWrapper>
  )
}

export default CustomerDetails
